import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = (event) => {
    const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;
    const shouldAutoOpenOnMobile = this.hasAttribute('auto-open-mobile') && isMobileViewport;
    const shouldForceOpenOnMobile = isMobileViewport;

    if (!(this.hasAttribute('auto-open') || shouldAutoOpenOnMobile || shouldForceOpenOnMobile)) return;

    // cart-items-component owns the morph of its section. Opening on the bare
    // CartAddEvent would show the previous (empty) state for a frame until that
    // morph lands — the "Empty flash". Instead of morphing here too (double
    // morph), wait for cart-items to emit CartRenderedEvent for this section and
    // open then, so the item is already present. When the event carries no
    // section for this drawer, there is nothing to wait for and we open now.
    const sections = event?.detail?.data?.sections;
    const cartItems = this.querySelector('cart-items-component');
    const sectionId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : null;

    if (!(sections && sectionId && sections[sectionId])) {
      this.showDialog();
      return;
    }

    let opened = false;
    const open = () => {
      if (opened) return;
      opened = true;
      clearTimeout(fallbackTimer);
      document.removeEventListener(ThemeEvents.cartRendered, onRendered);
      this.showDialog();
    };

    /** @param {Event} renderedEvent */
    const onRendered = (renderedEvent) => {
      const renderedSectionId = /** @type {CustomEvent} */ (renderedEvent).detail?.sectionId;
      if (renderedSectionId === sectionId) open();
    };

    // Open regardless if the render signal never arrives (e.g. a morph error),
    // so the drawer can't get stuck closed after an add.
    const fallbackTimer = setTimeout(open, 1000);
    document.addEventListener(ThemeEvents.cartRendered, onRendered);
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
